/** Fisher-Yates over indices 0..length-1; identity order when not shuffling. */
export function buildOrder(length: number, shuffle: boolean, random: () => number = Math.random): number[] {
  const order = Array.from({ length }, (_, i) => i);
  if (!shuffle) return order;
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

/**
 * Next position in a playlist, wrapping when `loop`. Returns null when the list
 * is exhausted going forward; going backward it stays on the first entry.
 */
export function stepPosition(position: number, length: number, direction: 1 | -1, loop: boolean): number | null {
  if (length <= 0) return null;
  const next = position + direction;
  if (next >= length) return loop ? 0 : null;
  if (next < 0) return loop ? length - 1 : 0;
  return next;
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'svg', 'apng', 'ico']);

export function isUrlRef(ref: string): boolean {
  return /^(https?:|blob:|data:|\/|\.\.?\/)/i.test(ref);
}

/** Classifies a URL by file extension; anything unknown is treated as media. */
export function kindFromUrl(url: string): 'image' | 'media' {
  if (/^data:image\//i.test(url)) return 'image';
  const path = url.split(/[?#]/)[0];
  const ext = path.includes('.') ? path.slice(path.lastIndexOf('.') + 1).toLowerCase() : '';
  return IMAGE_EXTENSIONS.has(ext) ? 'image' : 'media';
}

/**
 * Order for the next shuffled cycle: a fresh permutation whose first entry is
 * not the one that just played, so a reshuffle never repeats an item back to back.
 */
export function nextCycleOrder(previous: readonly number[], random: () => number = Math.random): number[] {
  const order = buildOrder(previous.length, true, random);
  if (order.length > 1 && order[0] === previous[previous.length - 1]) {
    const swapWith = 1 + Math.floor(random() * (order.length - 1));
    [order[0], order[swapWith]] = [order[swapWith], order[0]];
  }
  return order;
}
